/**
 * Tape-level status groupings used by the Processing sliders and the Media Metrics tiles.
 *
 * Source of truth: saas/lambdas/arkbridge_constants.py (IDs 0–13).
 *
 * ── Filter by ID, never by name ──────────────────────────────────────────────
 * There are THREE different names for the same status, so a name is not a safe key:
 *
 *   ID | arkbridge_constants mnemonic | noah.status.name (the DB table) | vwItemsAPI.Status
 *   ---|------------------------------|---------------------------------|------------------
 *    0 | Unknown                      | —                               | Unknown
 *    1 | Created  (DB holds 'Pending')| Created                         | Pending
 *    2 | Cataloged                    | Cataloged                       | Catalogued
 *    3 | Read                         | Read Success                    | Read
 *    4 | Uploaded                     | Upload Success                  | Uploaded
 *    5 | loaded                       | Loaded in Drive                 | Unknown  ← CASE gap
 *    6 | in_progress                  | Read in Progress                | Unknown  ← CASE gap
 *    7 | read_fail                    | Read Fail                       | Unknown  ← CASE gap
 *    8 | unloaded                     | Unloaded from Drive             | Unknown  ← CASE gap
 *    9 | extract_in_progress          | Extract in Progress             | Unknown  ← CASE gap
 *   10 | extract_fail                 | Extract Failed                  | Unknown  ← CASE gap
 *   11 | extract_ok                   | Extract Success                 | Unknown  ← CASE gap
 *   12 | upload_in_progress           | Upload in Progress              | Unknown  ← CASE gap
 *   13 | upload_fail                  | Upload Failed                   | Unknown  ← CASE gap
 *
 * `man_item_count` filters with `lower(Status) in (...)` against the LAST column, and
 * `vwItemsAPI` derives that column with a CASE that stops at 4:
 *
 *   CASE i.Status WHEN 1 THEN 'Pending' WHEN 2 THEN 'Catalogued' WHEN 3 THEN 'Read'
 *                 WHEN 4 THEN 'Uploaded' ELSE 'Unknown' END AS Status
 *
 * So every status from 5 upward collapses to the literal 'Unknown' and matches no name.
 * Filtering by name silently returned 0 for the In Progress and Read Failed tiles, and
 * under-counted Audit/Catalogued and Virtualized. `StatusID` on the same view carries the
 * raw integer, which is why every group below is a list of IDs sent as `status_ids`.
 *
 * This file owns status *grouping* for counting. Status *display* lives in
 * utils/tapeStatus.js, which maps the same ids to the `noah.status` names — never pass those
 * names to a count call.
 */
import {TAPE_STATUS_DISPLAY_NAMES} from "../../../../../utils/tapeStatus";

/**
 * Statuses that indicate a tape's ingestion is complete — it has been virtualized.
 *
 * Upload Success only, by requirement: "Virtualized/Uploaded" is read strictly as "the content
 * reached the cloud", which is exactly what status 4 records.
 *
 * Known trade-off, deliberately accepted. Extract runs *after* upload on the projects that need
 * it (backup_saas/Docs.md 'Status Values' note), so a tape that has moved on to 11 (Extract
 * Success) has already been uploaded, yet no longer counts here. On an extract-style project the
 * tile therefore undercounts, and a tape can appear to leave the Virtualized total by making
 * progress. Locally this is currently a no-op — 1 tape at status 4, 0 at status 11 — but it will
 * bite the first customer whose workflow extracts. Restoring 11 to this list is the whole fix.
 */
export const TAPE_INGESTION_COMPLETE_STATUS_IDS = [
    4,  // Uploaded / Upload Success
];

/**
 * Lowest status id that counts as audited. IDs 0 and 1 (`Unknown`, `Created`) sit below it and
 * belong to no tile: they are the denominator, never a numerator.
 */
export const AUDIT_COMPLETE_MIN_STATUS_ID = 2;

/**
 * Statuses that indicate a tape has completed audit (catalogued or beyond) — every id from
 * {@link AUDIT_COMPLETE_MIN_STATUS_ID} upwards. Any tape past `Created` has been audited,
 * including every ingestion and error status, because reaching them requires having been
 * catalogued first.
 *
 * Derived from the status map rather than written out, so adding a status to ArkBridge is a
 * one-place change: put it in TAPE_STATUS_DISPLAY_NAMES and the tile counts it, instead of the
 * count silently ignoring it. Note what that assumes — that anything numbered above 2 sits
 * after cataloguing. True while ArkBridge's statuses stay chronological; a future status that
 * belongs BEFORE catalogue would need excluding here explicitly.
 *
 * The ids are still enumerated by the time they reach the API. `man_item_count` accepts only
 * `status_ids` and renders `StatusID in (…)`, so the `>=` cannot be pushed down into the SQL
 * without a new API parameter. Identical rows either way while 13 is the highest status.
 */
export const TAPE_AUDIT_COMPLETE_STATUS_IDS = Object.keys(TAPE_STATUS_DISPLAY_NAMES)
    .map(Number)
    .filter(id => id >= AUDIT_COMPLETE_MIN_STATUS_ID)
    .sort((a, b) => a - b);

/**
 * The status that means a tape is actively being read. Backs the Media Metrics "Reading" tile.
 *
 * Specified as 6 (Read in Progress) and nothing else. It previously also counted 5 (Loaded in Drive),
 * 9 (Extract in Progress) and 12 (Upload in Progress) as mid-flight, which made the tile a general
 * measure of pipeline activity rather than the read count its label promises — a tape sitting in a
 * drive doing nothing was reported as being read.
 *
 * The consequence worth knowing: 5, 9 and 12 now appear in no tile at all, so a tape loaded in a
 * drive, mid-extract or mid-upload is invisible between Audit/Catalogued and Virtualized/Uploaded.
 * 8 (Unloaded from Drive) was already invisible for the same reason. Closing that gap wants its own
 * tile rather than a widened Reading count; noted in media-metrics-status-mapping.md.
 */
export const TAPE_READ_IN_PROGRESS_STATUS_IDS = [
    6, // in_progress / Read in Progress
];

/**
 * Statuses at which media has finished with the reading process, one way or the other.
 *
 * 4 is Upload Success and 7 is Read Fail — the two terminal outcomes of a read attempt.
 *
 * Backs no tile today. In Library was specified as the complement of this list and no longer is: it is
 * now `StatusID >= 2`, so a concluded read counts as shelved like any other catalogued tape. Kept
 * because the pair is a fact about ArkBridge worth naming, and because "which statuses end a read" is
 * the question any future tile about outstanding work will ask.
 */
export const READ_CONCLUDED_STATUS_IDS = [4, 7];

/**
 * Statuses at which nothing further will be attempted on a tape. Backs the Done Tapes figure.
 *
 * Specified as 4 and 13 — Upload Success and Upload Failed, the two ends of the upload rather than of the
 * read. So this is NOT {@link READ_CONCLUDED_STATUS_IDS}, which is 4 and 7: a tape the drive could not read
 * (7) is not done, because a re-read is what happens next.
 *
 * Written out rather than derived from the two constants it happens to equal today —
 * {@link TAPE_INGESTION_COMPLETE_STATUS_IDS} and {@link TAPE_READ_FAILED_STATUS_IDS}, the statuses behind
 * the Virtualized/Uploaded and Read Failed tiles. The two readings agree now and are free to diverge:
 * ArkBridge models three failure modes (7, 10, 13) and widening the Read Failed tile to all of them is an
 * open question, at which point that tile would count a read that will be retried while this list should
 * not. A test pins the two against each other so the divergence is noticed rather than inherited.
 *
 * Done Tapes has no tile of its own; it exists as an input to the Percentage calculation. See
 * DONE_TAPES_FILTER for the metadata half of the condition, which is what makes it 'done AND on a shelf'.
 */
export const TAPE_DONE_STATUS_IDS = [
    4,  // upload_success / Upload Success
    13, // upload_fail / Upload Failed
];

/**
 * Statuses at which media counts as being in the library. Backs the "In Library" tile, together with
 * the location phrase the widget matches in Metadata.
 *
 * Specified as `StatusID >= 2` — every status from cataloguing onwards, which makes this list identical
 * to {@link TAPE_AUDIT_COMPLETE_STATUS_IDS} today. It is derived separately rather than aliased because
 * the two answer different questions and are free to diverge: this one is "is it on a shelf", that one
 * is "has it been catalogued".
 *
 * Changed from the complement of {@link READ_CONCLUDED_STATUS_IDS} (`not in (4,7)`), by requirement.
 * Two consequences, both intended and neither visible on the tile:
 *
 *   - A tape that has been uploaded (4) or failed its read (7) now counts as in the library. It is
 *     still a physical cartridge on a shelf, which is what the tile is about; before, finishing its
 *     read took it out of the count.
 *   - 0 (Unknown) and 1 (Created) are now excluded rather than included. They were only ever in as the
 *     literal reading of `not in (4,7)`, and were kept out in practice by the location phrase: media
 *     that has not been catalogued has no location written against it.
 *
 * Derived from the threshold rather than written out, so a status added to ArkBridge is counted the
 * moment it lands in the map — with the same assumption {@link TAPE_AUDIT_COMPLETE_STATUS_IDS} makes,
 * that anything numbered above 2 sits after cataloguing.
 */
export const TAPE_IN_LIBRARY_STATUS_IDS = Object.keys(TAPE_STATUS_DISPLAY_NAMES)
    .map(Number)
    .filter(id => id >= AUDIT_COMPLETE_MIN_STATUS_ID)
    .sort((a, b) => a - b);

/**
 * Statuses that indicate a tape could not be processed. Backs the Media Metrics "Read Failed" tile.
 *
 * Specified as 13 (Upload Failed), by requirement — changed from 7 (Read Fail), which is what this
 * group held until now. So the tile reports the failure at the far end of the pipeline rather than
 * the one at the drive, and the label is now wider than the status behind it.
 *
 * What that means for the count, stated plainly because nothing else on screen says it: a cartridge
 * the drive could not read (7) is no longer reported as a failure anywhere. It counts under
 * Audit/Catalogued and, since In Library became `StatusID >= 2`, under In Library as well — so it is a
 * tape with a known problem that no tile names as one.
 *
 * ArkBridge models three distinct failure modes — 7 read_fail, 10 extract_fail, 13 upload_fail — and
 * one tile can only show one of them; 10 has never been surfaced. Counting all three is the real fix
 * and needs a decision on the tile's wording. Open product question, see media-metrics-status-mapping.md.
 */
export const TAPE_READ_FAILED_STATUS_IDS = [
    13, // upload_fail / Upload Failed
];
